<script setup lang="ts">
// Shared site header (top app-bar + mobile navigation drawer) for every layout.
// Extracted from layouts/default.vue and layouts/full-width.vue, which had drifted
// apart (different menu items, "Account" vs "Overview" label, avatar shown in one
// only). Layouts now render <AppHeader /> and differ only in content width.

const { status, data } = useAuth()

// full-width pages want the bar to span edge-to-edge like their content.
const { fluid = false } = defineProps<{ fluid?: boolean }>()

const drawer = ref(false)

const menuItems = computed<Array<{ title: string, icon: string, to: string, subtitle?: string }>>(() => {
  if (status.value === 'authenticated') {
    return [
      { title: 'My Uploads', icon: 'mdi-map-marker-path', to: '/account/uploads', subtitle: 'Your uploaded GPS tracks and sessions' },
      { title: 'Challenges', icon: 'mdi-trophy-outline', to: '/challenges', subtitle: 'Open Challenge and League — two free ways to race other sailors' },
      // { title: 'Virtual Racing', icon: 'mdi-sail-boat', to: '/league', subtitle: 'GPS-tracked races with virtual marks and Portsmouth Yardstick handicap' },
      { title: 'Events', icon: 'mdi-calendar', to: '/competitions', subtitle: 'Browse and enter regattas and club events' },
      { title: 'Resources', icon: 'mdi-bookshelf', to: '/resources', subtitle: 'Guides, documentation and sailing tools' },
    ]
  }
  return [
    { title: 'Explore', icon: 'mdi-compass-outline', to: '/sails', subtitle: 'Browse GPS-tracked sailing sessions from sailors around the world' },
    { title: 'Challenges', icon: 'mdi-trophy-outline', to: '/challenges', subtitle: 'Open Challenge and League — two free ways to race other sailors' },
    // { title: 'Virtual Racing', icon: 'mdi-sail-boat', to: '/league', subtitle: 'GPS-tracked races with virtual marks and Portsmouth Yardstick handicap' },
    { title: 'Events', icon: 'mdi-calendar', to: '/competitions', subtitle: 'Browse and enter regattas and club events' },
    { title: 'App', icon: 'mdi-cellphone', to: '/app', subtitle: 'Track your sailing live from your phone' },
    { title: 'Resources', icon: 'mdi-bookshelf', to: '/resources', subtitle: 'Guides, documentation and sailing tools' },
  ]
})

const logout = async () => {
  const { signOut } = useAuth()
  try {
    await signOut({ callbackUrl: '/' })
  } catch {
    await navigateTo('/')
  }
}
</script>

<template>
  <v-no-ssr>
    <v-app-bar app absolute style="height: 64px;">
      <v-container :class="['fill-height', fluid ? 'py-0 px-4' : 'pa-0']" :fluid="fluid">
        <span class="hidden-lg-and-up">
          <v-app-bar-nav-icon aria-label="Open navigation menu" @click="drawer = !drawer" />
        </span>

        <nuxt-link :to="status === 'authenticated' ? '/account' : '/'" aria-label="SailingMetrics">
          <v-img
              class="mx-2"
              src="/icon_rio.svg"
              alt=""
              height="30"
              width="30"
              contain
              eager
          />
        </nuxt-link>

        <nuxt-link :to="status === 'authenticated' ? '/account' : '/'">
          <v-toolbar-title>
            SailingMetrics
          </v-toolbar-title>
        </nuxt-link>
        <v-spacer />
        <v-toolbar-items class="d-none d-lg-flex">
          <template v-for="(item, index) in menuItems" :key="`btn-${index}`">
            <v-tooltip :text="item.subtitle" :aria-label="item.subtitle" location="bottom" :open-delay="300">
              <template #activator="{ props }">
                <v-btn
                    v-bind="props"
                    variant="text"
                    :to="item.to"
                    :prepend-icon="item.icon"
                    nuxt
                >
                  {{ item.title }}
                </v-btn>
              </template>
            </v-tooltip>
          </template>
          <v-btn
              v-if="status === 'authenticated'"
              variant="text"
              to="/account"
              nuxt
              class="px-2"
          >
            <Avatar :size="28" :src="data?.user?.avatar_url ?? null" :name="data?.user?.name ?? ''" class="mr-2" />
            Overview
          </v-btn>
          <v-btn
              v-if="status === 'authenticated'"
              variant="text"
              prepend-icon="mdi-logout"
              @click="logout"
          >Logout</v-btn>
        </v-toolbar-items>
        <v-btn
            v-show="status !== 'authenticated'"
            color="primary"
            class="mx-2"
            to="/login"
            variant="outlined"
            prepend-icon="mdi-login"
            nuxt
        >
          Login
        </v-btn>
        <v-btn
            v-show="status !== 'authenticated'"
            color="primary"
            class="ma-2 hidden-sm-and-down"
            href="/account/signup/"
            variant="elevated"
            nuxt
        >
          Sign up
        </v-btn>
      </v-container>
    </v-app-bar>

    <v-navigation-drawer
        v-model="drawer"
        app
        temporary
    >
      <v-list nav>
        <v-list-item
            v-if="status !== 'authenticated'"
            to="/login"
            prepend-icon="mdi-login"
        >
          <v-list-item-title>
            Login
          </v-list-item-title>
        </v-list-item>
        <v-list-item
            v-if="status !== 'authenticated'"
            to="/account/signup"
            prepend-icon="mdi-account-plus"
        >
          <v-list-item-title>
            Sign up
          </v-list-item-title>
        </v-list-item>
        <v-divider v-if="status !== 'authenticated'" class="mb-2" />
        <template
            v-for="(item, index) in menuItems"
            :key="`list-${index}`"
        >
          <v-list-item
              nuxt
              :to="item.to"
              :exact-path="true"
              :title="item.title"
              :subtitle="item.subtitle"
              lines="two"
          >
            <template #prepend>
              <v-icon>{{ item.icon }}</v-icon>
            </template>
          </v-list-item>
        </template>
        <v-list-item
            v-if="status === 'authenticated'"
            nuxt
            to="/account"
            :exact-path="true"
        >
          <template #prepend>
            <Avatar :size="28" :src="data?.user?.avatar_url ?? null" :name="data?.user?.name ?? ''" class="mr-3" />
          </template>
          <v-list-item-title>Overview</v-list-item-title>
        </v-list-item>
        <v-list-item
            v-if="status === 'authenticated'"
            title="Logout"
            prepend-icon="mdi-logout"
            @click="logout"
        />
      </v-list>
    </v-navigation-drawer>
  </v-no-ssr>
</template>
